import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Flame, Globe2, Truck,
  Layers, Boxes, Receipt, Calculator, UserCog, LogOut, Coffee,
  ChevronLeft, ChevronRight, Settings as SettingsIcon, Menu, X,
  FileSpreadsheet, BookOpen, Target, Wrench, Tag, Lock, TrendingDown,
  UploadCloud, ShieldCheck, Plug, FileText,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { mod: "dashboard",     to: "/",                label: "Dashboard",        icon: LayoutDashboard, group: "Overview" },
  { mod: "roasts",        to: "/roasts",          label: "Roast Profiles",   icon: Flame,           group: "Operations" },
  { mod: "imports",       to: "/imports",         label: "ProRoast Imports", icon: UploadCloud,     group: "Operations" },
  { mod: "roasts",        to: "/weekly-roast-report", label: "Weekly Roast Report", icon: FileSpreadsheet, group: "Operations" },
  { mod: "roasts",        to: "/maintenance",     label: "Maintenance",      icon: ShieldCheck,     group: "Operations" },
  // /myroast (Tablet iframe) is intentionally hidden from the sidebar —
  // the route still works directly if you need to spot-check the tablet
  // bundle, but it's not a daily-use surface for MA users.
  { mod: "inventory",     to: "/inventory",       label: "Green Inventory",  icon: Boxes,           group: "Operations" },
  { mod: "inventory",     to: "/inventory-forecast", label: "Inventory Forecast", icon: TrendingDown,  group: "Operations" },
  { mod: "cupping",       to: "/cupping",         label: "Cupping",          icon: Coffee,          group: "Operations" },
  { mod: "sales_targets", to: "/sales-targets",   label: "Sales & Targets",  icon: Target,          group: "Operations" },
  { mod: "monthly_costs", to: "/monthly-costs",   label: "Monthly Costs",    icon: Receipt,         group: "Costing" },
  { mod: "equipment",     to: "/equipment",       label: "Labour & Equipment", icon: Wrench,        group: "Costing" },
  { mod: "manufacturing_cost", to: "/manufacturing-cost", label: "Manufacturing Cost", icon: Calculator, group: "Costing" },
  { mod: "bills",         to: "/bills",           label: "Zoho Bills Inbox", icon: FileSpreadsheet, group: "Bookkeeping" },
  { mod: "journals",      to: "/journals",        label: "Manual Journals",  icon: BookOpen,        group: "Bookkeeping" },
  { mod: "monthly_report",to: "/monthly-report",  label: "Monthly Report",   icon: FileText,        group: "Bookkeeping" },
  { mod: "origins",       to: "/origins",         label: "Origins",          icon: Globe2,          group: "Master Data" },
  { mod: "suppliers",     to: "/suppliers",       label: "Suppliers",        icon: Truck,           group: "Master Data" },
  { mod: "machines",      to: "/machines",        label: "Machines",         icon: Wrench,          group: "Master Data" },
  { mod: "operators",     to: "/operators",       label: "Operators",        icon: UserCog,         group: "Master Data" },
  { mod: "blends",        to: "/blends",          label: "Recipes / Blends", icon: Layers,          group: "Master Data" },
  { mod: "item_mapping",  to: "/item-mapping",    label: "Item Mapping",     icon: Tag,             group: "Master Data" },
  { mod: "users",         to: "/users",           label: "Users",            icon: UserCog,         group: "Admin" },
  { mod: "users",         to: "/roles",           label: "Roles",            icon: Lock,            group: "Admin" },
  { mod: "settings",      to: "/settings",        label: "Integrations",     icon: SettingsIcon,    group: "Admin" },
  { mod: "settings",      to: "/admin/integrations", label: "Email (Resend)",  icon: Plug,           group: "Admin" },
];

const STORE_KEY = "bcr_sidebar_collapsed";

export default function Layout({ children }) {
  const { user, can, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORE_KEY) === "1");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Close mobile drawer whenever route changes
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const visible = NAV.filter((n) => can(n.mod));
  const groups = visible.reduce((acc, n) => {
    (acc[n.group] = acc[n.group] || []).push(n);
    return acc;
  }, {});

  // Desktop width
  const W_DESK = collapsed ? "lg:w-16" : "lg:w-64";
  const ML_DESK = collapsed ? "lg:ml-16" : "lg:ml-64";

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 flex items-center justify-between px-4 border-b"
           style={{ background: "hsl(var(--sidebar))", color: "hsl(var(--sidebar-foreground))",
                    borderColor: "hsl(var(--sidebar-border))" }}
           data-testid="mobile-topbar">
        <button onClick={() => setMobileOpen(true)} className="p-2 -ml-2" data-testid="mobile-menu-toggle"
                aria-label="Open menu">
          <Menu className="w-5 h-5" />
        </button>
        <img src="/favicon.png" alt="BCR" className="h-7 w-7 object-contain" />
        <button onClick={async () => { await logout(); nav("/login"); }}
                className="p-2 -mr-2" aria-label="Sign out" title="Sign out"
                data-testid="mobile-logout">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/60"
          data-testid="mobile-backdrop"
          aria-label="Close menu"
        />
      )}

      {/* Sidebar — drawer on mobile, fixed on desktop */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col transition-transform duration-200
                    w-72 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
                    lg:translate-x-0 ${W_DESK}`}
        style={{ background: "hsl(var(--sidebar))", color: "hsl(var(--sidebar-foreground))" }}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <div className={`relative pt-7 pb-5 border-b ${collapsed ? "lg:px-3" : "lg:px-6"} px-6`}
             style={{ borderColor: "hsl(var(--sidebar-border))" }}>
          {/* Mobile-only close button */}
          <button onClick={() => setMobileOpen(false)} className="lg:hidden absolute top-3 right-3 p-1.5"
                  aria-label="Close menu">
            <X className="w-4 h-4" />
          </button>

          {collapsed ? (
            <img src="/favicon.png" alt="BCR" className="hidden lg:block h-8 w-8 mx-auto object-contain" />
          ) : null}
          <div className={collapsed ? "lg:hidden" : ""}>
            <img
              src="https://www.brotherscoffee.co.za/BCR_W%202026.png"
              alt="Brothers Coffee Roastery"
              className="h-10 w-auto mb-3 object-contain"
              style={{ maxWidth: "100%" }}
            />
            <p className="label-tiny" style={{ color: "hsl(var(--sidebar-muted))" }}>
              Manufacturing Suite
            </p>
          </div>

          {/* Desktop-only collapse button */}
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="sidebar-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden lg:flex absolute -right-3 top-9 w-6 h-6 rounded-full border items-center justify-center"
            style={{ background: "hsl(var(--sidebar))", borderColor: "hsl(var(--sidebar-border))",
                     color: "hsl(var(--sidebar-foreground))" }}
          >
            {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </button>
        </div>

        <nav className={`flex-1 overflow-y-auto py-4 space-y-5 px-3 ${collapsed ? "lg:px-2" : "lg:px-3"}`}>
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <p className={`px-3 mb-2 label-tiny ${collapsed ? "lg:hidden" : ""}`}
                 style={{ color: "hsl(var(--sidebar-muted))" }}>
                {group}
              </p>
              <ul className="space-y-0.5">
                {items.map((n) => (
                  <li key={n.to}>
                    <NavLink
                      to={n.to}
                      end={n.to === "/"}
                      data-testid={`nav-${n.mod}-${n.to.replace(/\//g, "-")}`}
                      title={collapsed ? n.label : undefined}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors
                         ${collapsed ? "lg:justify-center lg:px-0 lg:gap-0" : ""}
                         ${isActive
                            ? "bg-[hsl(20,39%,39%)] text-white"
                            : "text-[hsl(36,25%,90%)] hover:bg-[hsl(20,12%,16%)]"}`
                      }
                    >
                      <n.icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
                      <span className={collapsed ? "lg:hidden" : ""}>{n.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className={`py-4 border-t px-3 ${collapsed ? "lg:px-2" : "lg:px-3"}`}
             style={{ borderColor: "hsl(var(--sidebar-border))" }}>
          <div className={`px-3 mb-2 ${collapsed ? "lg:hidden" : ""}`}>
            <p className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</p>
            <p className="text-xs truncate" style={{ color: "hsl(var(--sidebar-muted))" }}>
              {user?.email}
            </p>
            <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: "hsl(var(--accent))" }}>
              {user?.role}
            </p>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); nav("/login"); }}
            title={collapsed ? "Sign out" : undefined}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded text-[hsl(36,25%,90%)] hover:bg-[hsl(20,12%,16%)]
                        ${collapsed ? "lg:justify-center lg:px-0 lg:gap-0" : ""}`}
          >
            <LogOut className="w-4 h-4" strokeWidth={1.7} />
            <span className={collapsed ? "lg:hidden" : ""}>Sign out</span>
          </button>
        </div>
      </aside>

      <main className={`flex-1 min-w-0 transition-[margin] duration-200 pt-14 lg:pt-0 ${ML_DESK}`}>
        <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
